import React from "react";
import { useTranslation } from "react-i18next";

import { RowInvoice } from "../../types";
import { priceToFormat, getHstTax, formatWeight } from "../../helpers/invoice";
import { HST_TAX } from "../../consts";
import InvoiceRows from "../InvoiceRows";

import styles from "./InvoiceSummaryDetails.module.scss";

interface RowInvoiceConfig extends RowInvoice {
  isHidden?: boolean;
}

interface Props {
  subtotal: number;
  adjustmentSum?: number;
  hstTax?: number;
  grandTotal?: number;
  isHSTExemptChecked?: boolean;
  isWithTransactions?: boolean;
  hstRegistrationNumber?: string;
  invoiceFeeSubTotal?: number;
  transactionFeeSubTotal?: number;
  paymentBalanceSubTotal?: number;
  totalWeight?: number;
}

const InvoiceSummaryDetails = ({
  subtotal,
  adjustmentSum = 0,
  hstTax = HST_TAX,
  grandTotal,
  invoiceFeeSubTotal = 0,
  transactionFeeSubTotal = 0,
  paymentBalanceSubTotal = 0,
  isHSTExemptChecked = false,
  isWithTransactions,
  hstRegistrationNumber,
  totalWeight = 0,
}: Props) => {
  const { t } = useTranslation();

  const resultSum =
    adjustmentSum +
    subtotal +
    transactionFeeSubTotal +
    invoiceFeeSubTotal +
    paymentBalanceSubTotal;
  const hstTaxValue = getHstTax(resultSum, hstTax, isHSTExemptChecked);
  const rows: RowInvoiceConfig[] = [
    {
      name: t(
        `invoices.${isWithTransactions ? "transactions" : "transfers"}Subtotal`
      ),
      value: priceToFormat(subtotal),
      withBorder:
        !adjustmentSum &&
        !invoiceFeeSubTotal &&
        !transactionFeeSubTotal &&
        !paymentBalanceSubTotal,
    },
    {
      name: t("invoices.adjustmentTotal"),
      value: priceToFormat(adjustmentSum, true),
      isHidden: !adjustmentSum,
      withBorder:
        !invoiceFeeSubTotal &&
        !transactionFeeSubTotal &&
        !paymentBalanceSubTotal,
    },
    {
      name: t("invoices.transactionFeeSubTotal"),
      value: priceToFormat(transactionFeeSubTotal, true),
      isHidden: !transactionFeeSubTotal,
      withBorder: !invoiceFeeSubTotal && !paymentBalanceSubTotal,
      isRedValue: transactionFeeSubTotal < 0,
    },
    {
      name: t("invoices.invoiceFeeSubTotal"),
      value: priceToFormat(invoiceFeeSubTotal, true),
      isHidden: !invoiceFeeSubTotal,
      withBorder: !paymentBalanceSubTotal,
      isRedValue: invoiceFeeSubTotal < 0,
    },
    {
      name: t("invoices.balanceAdjustmentsSubTotal"),
      value: priceToFormat(paymentBalanceSubTotal, true),
      withBorder: true,
      isRedValue: paymentBalanceSubTotal < 0,
      isHidden: !paymentBalanceSubTotal,
    },
    {
      name: t("invoices.total"),
      value: priceToFormat(resultSum),
      withBoldBorder: isHSTExemptChecked,
      isSubtotal: true,
    },
    {
      name: t("invoices.hst", { hstRegistrationNumber }),
      value: priceToFormat(hstTaxValue),
      isHidden: isHSTExemptChecked,
      withBoldBorder: true,
    },
    {
      name: t("invoices.gTotal"),
      value: priceToFormat(grandTotal || resultSum + hstTaxValue),
      isGrandTotal: true,
    },
    {
      name: t("invoices.totalWeight"),
      value: formatWeight(totalWeight),
      isHidden: !totalWeight,
      isGrandTotal: true,
    },
  ];
  const visibleRows = rows.filter((r) => !r.isHidden);

  return (
    <table className={styles.summaryDetails}>
      <tbody>
        {visibleRows.map((row) => (
          <InvoiceRows key={row.name as string} {...row} />
        ))}
      </tbody>
    </table>
  );
};

export default InvoiceSummaryDetails;
